/**
 * Okuma listesi — sistemin ANA EKRANI.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SIRALAMA BİR ÜRÜN KARARIDIR: EN KÖTÜ ÜSTTE.
 *
 * Eski sistemde liste alfabetikti ve yedi gündür sessiz olan saha 200 satırın
 * ortasında kayboluyordu — kimse fark etmedi. Burada sıralamayı sunucu
 * yapıyor (durum sırası ↓, sonra ad) ve ekranın en üstünde kaç sahanın sessiz
 * olduğu yazıyor. Kullanıcı listeyi taramak zorunda değil; sorun ona geliyor.
 *
 * ─── YAPILANDIRMA DEĞİL, GÖZLEM ─────────────────────────────────────────────
 * Bu ekran "veri gelmiyor" der ve sebebini SÖYLEMEZ — bilmiyoruz. Sebebin
 * bizde olduğu durumlar (pano yok, yoklama kapalı) Denetim ekranında ve
 * `not_expected` durumunda ayrı duruyor.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import {
  AramaKutusu,
  KapsamNotu,
  TavanUyarisi,
  YapilandirmaSekmeleri,
  useAramaYazici,
} from './parts.tsx';
import { Badge } from '../design/Status.tsx';
import { cx } from '../design/cx.ts';
import type { AkisDurumu } from './canli.ts';
import { Button } from '../design/Button.tsx';
import { Card, CardBody } from '../design/Card.tsx';
import { EmptyState, SkeletonList } from '../design/Feedback.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';
import {
  DURUM_SUNUMU,
  gecenSure,
  sessizMi,
  tankDolulugu,
  useOkumalar,
  type OkumaSahasi,
} from './okuma.ts';
import { AKIS_METNI, useCanliOkumalar } from './canli.ts';

export function OkumaListesi(): React.JSX.Element {
  const { q, sessiz } = useSearch({ from: '/korumali/' });
  const navigate = useNavigate();

  const git = useCallback(
    (yeni: { q?: string | undefined; sessiz?: true | undefined }) => {
      void navigate({
        to: '/',
        search: {
          ...(yeni.q === undefined ? {} : { q: yeni.q }),
          ...(yeni.sessiz === undefined ? {} : { sessiz: true as const }),
        },
        replace: true,
      });
    },
    [navigate],
  );
  const aramaDegisti = useAramaYazici(
    useCallback((deger: string | undefined) => git({ q: deger, sessiz }), [git, sessiz]),
  );

  const sorgu = useOkumalar({ ...(q === undefined ? {} : { q }), sessiz: sessiz === true });

  /*
   * Canlı akış YALNIZ filtresiz listede. Akış tüm sahaları taşıyor; filtreli
   * bir görünüme uygulamak, süzülmüş listeye süzme dışı satır sızdırırdı.
   * Filtre varken gösterge "canlı değil" diyor — yalan söylemektense
   * kısıtlamayı söylemek.
   */
  const filtresiz = q === undefined && sessiz !== true;
  const akis = useCanliOkumalar(filtresiz);

  return (
    <>
      <PageHeader
        title="Sahalar"
        description="Son okumalar ve bağlantı durumu"
        action={<CanliGosterge durum={akis} />}
      />
      <YapilandirmaSekmeleri />
      <KapsamNotu />
      <AramaKutusu
        deger={q ?? ''}
        degisti={aramaDegisti}
        ipucu="Saha adı, tesisat kodu veya şehir"
      />

      <PageBody>
        {sorgu.isPending && <SkeletonList rows={4} />}

        {sorgu.isError && (
          <EmptyState
            title="Okumalar yüklenemedi"
            description={
              sorgu.error instanceof Error ? sorgu.error.message : 'Bilinmeyen bir hata.'
            }
            action={<Button onClick={() => void sorgu.refetch()}>Tekrar dene</Button>}
          />
        )}

        {sorgu.data !== undefined && (
          <div className="flex flex-col gap-4">
            <Ozet
              sahalar={sorgu.data.sites}
              sessizAcik={sessiz === true}
              degistir={() => git({ q, sessiz: sessiz === true ? undefined : true })}
            />

            {sorgu.data.sites.length === 0 ? (
              <EmptyState
                title={sessiz === true ? 'Sessiz saha yok' : 'Gösterilecek saha yok'}
                description={
                  sessiz === true
                    ? 'Veri beklenen tüm sahalardan okuma geliyor.'
                    : 'Önce yapılandırma sekmesinden saha ekleyin.'
                }
              />
            ) : (
              <>
                <ul className="flex flex-col gap-3">
                  {sorgu.data.sites.map((saha) => (
                    <li key={saha.site_id}>
                      <OkumaKarti saha={saha} simdi={sorgu.data.now} />
                    </li>
                  ))}
                </ul>
                <TavanUyarisi sayi={sorgu.data.sites.length} />
              </>
            )}
          </div>
        )}
      </PageBody>
    </>
  );
}

/**
 * Özet — "kaç sahadan veri gelmiyor" sorusunun cevabı ekranın EN ÜSTÜNDE.
 *
 * Sayı tek başına yetmez, ORAN gerekir: 7/12 ile 7/400 aynı cümleyle
 * anlatılamaz (Denetim ekranında verilen kararın aynısı).
 */
function Ozet({
  sahalar,
  sessizAcik,
  degistir,
}: {
  readonly sahalar: readonly OkumaSahasi[];
  readonly sessizAcik: boolean;
  readonly degistir: () => void;
}): React.JSX.Element {
  const sessizSayisi = sahalar.filter((s) => sessizMi(s.data_state)).length;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-3">
      <p role="status" className="text-sm">
        {sessizSayisi === 0 ? (
          <span className="font-medium text-ink">
            {sahalar.length} sahanın hepsinden veri geliyor
          </span>
        ) : (
          <>
            <span className="font-medium text-ink">
              {sahalar.length} sahanın {sessizSayisi} tanesinden veri gelmiyor
            </span>
            <span className="block text-ink-muted">En kötü durumdakiler listenin başında.</span>
          </>
        )}
      </p>
      <Button
        size="sm"
        variant={sessizAcik ? 'primary' : 'secondary'}
        aria-pressed={sessizAcik}
        onClick={degistir}
      >
        Yalnız veri gelmeyenler
      </Button>
    </div>
  );
}

/**
 * Canlı göstergesi — üç hâl, üç metin.
 *
 * Nokta TEK BAŞINA anlam taşımıyor: yanında metin var ve `title` ile sebebi
 * yazıyor. "Canlı değil" hâli özellikle önemli — kullanıcı ekrandaki verinin
 * kendiliğinden tazelenmediğini bilmeli.
 */
function CanliGosterge({ durum }: { readonly durum: AkisDurumu }): React.JSX.Element {
  const metin = AKIS_METNI[durum];
  const renk = durum === 'bagli' ? 'bg-ok' : durum === 'baglaniyor' ? 'bg-warn' : 'bg-ink-muted';

  return (
    <span
      role="status"
      title={metin.ipucu}
      className="inline-flex shrink-0 items-center gap-1.5 text-xs text-ink-muted"
    >
      <span aria-hidden="true" className={cx('size-2 rounded-full', renk)} />
      {metin.etiket}
    </span>
  );
}

function OkumaKarti({
  saha,
  simdi,
}: {
  readonly saha: OkumaSahasi;
  readonly simdi: string;
}): React.JSX.Element {
  const durum = DURUM_SUNUMU[saha.data_state];
  const yas = gecenSure(saha.last_ok_at, simdi);

  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to="/sahalar/$sahaId"
              params={{ sahaId: String(saha.site_id) }}
              className="text-base font-semibold text-ink hover:text-brand-700"
            >
              {saha.name}
            </Link>
            <p className="mt-0.5 truncate text-sm text-ink-muted">
              {saha.addr_code}
              {saha.city !== null && saha.city !== '' && ` · ${saha.city}`}
            </p>
          </div>
          <Badge tone={durum.tone}>{durum.etiket}</Badge>
        </div>

        {/*
         * SON VERİ — "ne zamandan beri çevrimdışı" DEĞİL.
         * `offline_since` uydurmuyoruz [K-017]: son başarılı okuma 14:27 ve
         * aralık 5 dk ise saha 14:27–14:32 arasında düşmüştür; tek bir an
         * yazmak tahmini gerçek gibi sunmaktır.
         */}
        <p className="mt-2 text-sm text-ink-muted">
          {yas === null ? (
            <span>Henüz başarılı okuma yok</span>
          ) : (
            <>
              Son veri: <span className="font-medium text-ink">{yas}</span>
            </>
          )}
          {saha.consecutive_failures > 0 && (
            <span> · {saha.consecutive_failures} ardışık başarısız deneme</span>
          )}
        </p>

        {saha.tanks.length > 0 && (
          <ul className="mt-3 flex flex-col gap-2">
            {saha.tanks.map((tank) => {
              const doluluk = tankDolulugu(tank);
              return (
                <li key={tank.tank_id} className="text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-ink-muted">
                      Tank {tank.tank_number ?? '—'} · {tank.capacity_m3} m³
                    </span>
                    <span className="font-medium text-ink">
                      {doluluk.yuzde === null ? (
                        'ölçüm yok'
                      ) : (
                        <>
                          %{doluluk.yuzde.toFixed(1)}
                          {doluluk.kg !== null && ` · ${doluluk.kg} kg`}
                        </>
                      )}
                    </span>
                  </div>
                  {/*
                   * SEVİYE ÇUBUĞU — renk TEK BAŞINA anlam taşımıyor, sayı
                   * hemen üstünde. Çubuk yalnız hızlı taramak için.
                   */}
                  {doluluk.yuzde !== null && (
                    <div
                      className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
                      aria-hidden="true"
                    >
                      <div
                        className="h-full rounded-full bg-brand-500"
                        style={{ width: `${String(Math.min(Math.max(doluluk.yuzde, 0), 100))}%` }}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {saha.tank_count === 0 && (
          <p className="mt-2 text-sm text-ink-muted">Bu sahada tank tanımlı değil.</p>
        )}
      </CardBody>
    </Card>
  );
}
